"use client";

/**
 * Captures the original traffic source (referrer + UTM params) on first visit
 * and stores it in a cookie that survives OAuth redirects.
 *
 * Only fires once — if the cookie already exists, it's a no-op.
 * Uses "first-touch" attribution: the first external source wins.
 */

import { useEffect } from "react";
import {
  TRAFFIC_SOURCE_COOKIE,
  TRAFFIC_SOURCE_MAX_AGE_SECONDS,
  parseTrafficSource,
} from "@/lib/analytics/traffic-source";
import { reportClientError } from "@/lib/observability/report-client-error";

function hasCookie(name: string): boolean {
  return document.cookie
    .split(";")
    .some((c) => c.trim().startsWith(name + "="));
}

export function TrafficSourceTracker() {
  useEffect(() => {
    // Only capture on the very first visit (first-touch attribution)
    if (hasCookie(TRAFFIC_SOURCE_COOKIE)) return;

    const ts = parseTrafficSource(document.referrer, window.location.href);

    try {
      const value = encodeURIComponent(JSON.stringify(ts));
      document.cookie = `${TRAFFIC_SOURCE_COOKIE}=${value}; path=/; max-age=${TRAFFIC_SOURCE_MAX_AGE_SECONDS}; SameSite=Lax`;
    } catch (error) {
      console.error(
        "[TrafficSourceTracker] Failed to set traffic source cookie:",
        error
      );
      reportClientError(error, {
        source: "component.TrafficSourceTracker",
        feature: "analytics",
        action: "setTrafficSourceCookie",
        component: "TrafficSourceTracker",
        handled: true,
      });
    }
  }, []);

  return null;
}
